import type {SerializablePlainData} from '../../../history/domain/SerializablePlainData';
import {isSketchRelationContainer} from '../constraint/SketchRelationContainer';
import type {SketchRelationDefinition} from '../constraint/relationDefinition/SketchRelationDefinition';
import type {SketchRelationBranchHintRuntimePort} from '../../../../types/sketch/solver/SketchParametricSolveCoordinator';

/**
 * 关系稳定多解分支运行时适配器。
 *
 * 职责：从运行时草图对象的正式关系容器中去重收集关系，并在参数解事务内捕获、
 * 写入或恢复领域分支提示。派生尺寸关系视图不在 `getRelations()` 中，因此不会被误写。
 */
export class SketchRelationBranchHintRuntime implements SketchRelationBranchHintRuntimePort {
    /**
     * 捕获指定对象范围内的关系分支提示。
     *
     * @param objects 运行时点和草图实体。
     * @returns 关系持久 ID 到分支提示的快照。
     */
    public capture(objects: readonly unknown[]): ReadonlyMap<string, SerializablePlainData | null> {
        const captured = new Map<string, SerializablePlainData | null>();

        this.collectRelations(objects).forEach((relation, persistentId) => {
            captured.set(persistentId, relation.getBranchHint());
        });

        return captured;
    }

    /**
     * 写入求解器确认的关系分支提示。
     *
     * @param hints 求解后的关系分支提示。
     * @param objects 运行时点和草图实体。
     * @returns 实际命中的关系数量。
     */
    public apply(
        hints: ReadonlyMap<string, SerializablePlainData>,
        objects: readonly unknown[],
    ): number {
        return this.write(hints, objects);
    }

    /**
     * 恢复事务开始前的关系分支提示。
     *
     * @param hints 捕获的关系分支提示，null 表示原关系没有提示。
     * @param objects 运行时点和草图实体。
     * @returns 实际命中的关系数量。
     */
    public restore(
        hints: ReadonlyMap<string, SerializablePlainData | null>,
        objects: readonly unknown[],
    ): number {
        return this.write(hints, objects);
    }

    /**
     * 按关系持久 ID 写入领域状态。
     *
     * @param hints 待写入的提示集合。
     * @param objects 运行时点和草图实体。
     * @returns 实际命中的关系数量。
     */
    private write(
        hints: ReadonlyMap<string, SerializablePlainData | null>,
        objects: readonly unknown[],
    ): number {
        if (hints.size === 0) {
            return 0;
        }

        const relations = this.collectRelations(objects);
        let appliedCount = 0;

        hints.forEach((hint, persistentId) => {
            const relation = relations.get(persistentId);

            if (relation) {
                relation.setBranchHint(hint);
                appliedCount += 1;
            }
        });

        return appliedCount;
    }

    /**
     * 从正式关系容器收集关系，并按稳定 ID 去重。
     *
     * @param objects 运行时点和草图实体。
     * @returns 关系持久 ID 到领域对象的映射。
     */
    private collectRelations(
        objects: readonly unknown[],
    ): ReadonlyMap<string, SketchRelationDefinition> {
        const relations = new Map<string, SketchRelationDefinition>();

        objects.forEach((object) => {
            if (!object || typeof object !== 'object') {
                return;
            }

            const container = Reflect.get(object, 'constraints');

            if (!isSketchRelationContainer(container)) {
                return;
            }

            container.getRelations().forEach((relation) => {
                if (relation.persistentId.length > 0) {
                    relations.set(relation.persistentId, relation);
                }
            });
        });

        return relations;
    }
}
